import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Gift,
  History,
  Loader2,
  RefreshCw,
  RotateCcw,
} from "lucide-react";
import { Card, CardContent, CardHeader } from "../ui/card";
import { useAuth } from "../../context/AuthContext";
import { Language } from "../../utils/dictionary";
import { creditHistoryAxios } from "../../services/membership/MembershipAxios";
import type { CreditHistoryItem } from "../../types/membership";

interface CreditHistorySectionProps {
  variant?: "default" | "compact";
}

const copy = {
  title: {
    [Language.SPANISH]: "Historial de créditos",
    [Language.ENGLISH]: "Credit history",
    [Language.PORTUGUESE]: "Histórico de créditos",
  },
  empty: {
    [Language.SPANISH]: "Aún no hay movimientos de créditos.",
    [Language.ENGLISH]: "There are no credit movements yet.",
    [Language.PORTUGUESE]: "Ainda não há movimentações de créditos.",
  },
  loadMore: {
    [Language.SPANISH]: "Cargar más",
    [Language.ENGLISH]: "Load more",
    [Language.PORTUGUESE]: "Carregar mais",
  },
  loading: {
    [Language.SPANISH]: "Cargando historial",
    [Language.ENGLISH]: "Loading history",
    [Language.PORTUGUESE]: "Carregando histórico",
  },
  failed: {
    [Language.SPANISH]: "No pudimos cargar el historial.",
    [Language.ENGLISH]: "We could not load the history.",
    [Language.PORTUGUESE]: "Não conseguimos carregar o histórico.",
  },
  beforeAfter: {
    [Language.SPANISH]: "Saldo",
    [Language.ENGLISH]: "Balance",
    [Language.PORTUGUESE]: "Saldo",
  },
  initialBalance: {
    [Language.SPANISH]: "Saldo inicial",
    [Language.ENGLISH]: "Initial balance",
    [Language.PORTUGUESE]: "Saldo inicial",
  },
  reason: {
    [Language.SPANISH]: "Motivo",
    [Language.ENGLISH]: "Reason",
    [Language.PORTUGUESE]: "Motivo",
  },
};

const reasonLabels: Record<string, Record<Language, string>> = {
  membership_cycle: {
    [Language.SPANISH]: "Recarga de membresía",
    [Language.ENGLISH]: "Membership recharge",
    [Language.PORTUGUESE]: "Recarga de assinatura",
  },
  stripe_credit_purchase: {
    [Language.SPANISH]: "Compra de créditos",
    [Language.ENGLISH]: "Credit purchase",
    [Language.PORTUGUESE]: "Compra de créditos",
  },
  create_website: {
    [Language.SPANISH]: "Creación de embudo",
    [Language.ENGLISH]: "Funnel creation",
    [Language.PORTUGUESE]: "Criação de funil",
  },
  create_store: {
    [Language.SPANISH]: "Creación de tienda",
    [Language.ENGLISH]: "Store creation",
    [Language.PORTUGUESE]: "Criação de loja",
  },
  create_creative: {
    [Language.SPANISH]: "Imagen creativa",
    [Language.ENGLISH]: "Creative image",
    [Language.PORTUGUESE]: "Imagem criativa",
  },
  create_creatives: {
    [Language.SPANISH]: "Imágenes creativas",
    [Language.ENGLISH]: "Creative images",
    [Language.PORTUGUESE]: "Imagens criativas",
  },
  create_video_creative: {
    [Language.SPANISH]: "Video creativo",
    [Language.ENGLISH]: "Creative video",
    [Language.PORTUGUESE]: "Vídeo criativo",
  },
  create_video: {
    [Language.SPANISH]: "Video",
    [Language.ENGLISH]: "Video",
    [Language.PORTUGUESE]: "Vídeo",
  },
  approve_video_draft: {
    [Language.SPANISH]: "Video UGC aprobado",
    [Language.ENGLISH]: "Approved UGC video",
    [Language.PORTUGUESE]: "Vídeo UGC aprovado",
  },
  builder_image_edit: {
    [Language.SPANISH]: "Edición IA de imagen",
    [Language.ENGLISH]: "AI image edit",
    [Language.PORTUGUESE]: "Edição IA de imagem",
  },
  builder_html_edit: {
    [Language.SPANISH]: "Edición IA de sección",
    [Language.ENGLISH]: "AI section edit",
    [Language.PORTUGUESE]: "Edição IA de seção",
  },
  builder_page_edit: {
    [Language.SPANISH]: "Edición IA de embudo",
    [Language.ENGLISH]: "AI funnel edit",
    [Language.PORTUGUESE]: "Edição IA de funil",
  },
  meta_action_plan: {
    [Language.SPANISH]: "Plan de acción Meta",
    [Language.ENGLISH]: "Meta action plan",
    [Language.PORTUGUESE]: "Plano de ação Meta",
  },
  rating_funnel_reward: {
    [Language.SPANISH]: "Bonificación por calificar embudo",
    [Language.ENGLISH]: "Funnel rating reward",
    [Language.PORTUGUESE]: "Bônus por avaliar funil",
  },
  manual_credit_add: {
    [Language.SPANISH]: "Créditos agregados manualmente",
    [Language.ENGLISH]: "Credits added manually",
    [Language.PORTUGUESE]: "Créditos adicionados manualmente",
  },
  manual_credit_subtract: {
    [Language.SPANISH]: "Ajuste manual de créditos",
    [Language.ENGLISH]: "Manual credit adjustment",
    [Language.PORTUGUESE]: "Ajuste manual de créditos",
  },
  payment_refund_reversal: {
    [Language.SPANISH]: "Reversa por reembolso de compra",
    [Language.ENGLISH]: "Purchase refund reversal",
    [Language.PORTUGUESE]: "Reversão por reembolso de compra",
  },
};

const movementLabels: Record<string, Record<Language, string>> = {
  credit_refund: {
    [Language.SPANISH]: "Créditos devueltos",
    [Language.ENGLISH]: "Credits refunded",
    [Language.PORTUGUESE]: "Créditos devolvidos",
  },
  credit_refund_reversal: {
    [Language.SPANISH]: "Re-cobro de créditos",
    [Language.ENGLISH]: "Credits re-deducted",
    [Language.PORTUGUESE]: "Créditos recobrados",
  },
  credit_addition: {
    [Language.SPANISH]: "Créditos agregados",
    [Language.ENGLISH]: "Credits added",
    [Language.PORTUGUESE]: "Créditos adicionados",
  },
  debit: {
    [Language.SPANISH]: "Uso de créditos",
    [Language.ENGLISH]: "Credits used",
    [Language.PORTUGUESE]: "Uso de créditos",
  },
};

const localeByLanguage: Record<Language, string> = {
  [Language.SPANISH]: "es-CO",
  [Language.ENGLISH]: "en-US",
  [Language.PORTUGUESE]: "pt-BR",
};

const PAGE_SIZE = 20;

const labelFor = (item: CreditHistoryItem, language: Language) => {
  return (
    reasonLabels[item.reason_code]?.[language] ||
    movementLabels[item.movement_type]?.[language] ||
    item.reason_code.replace(/_/g, " ")
  );
};

const iconFor = (item: CreditHistoryItem) => {
  if (item.movement_type === "membership_cycle") return RefreshCw;
  if (item.movement_type === "credit_refund" || item.movement_type === "system_reward") return Gift;
  if (item.movement_type === "credit_refund_reversal" || item.movement_type === "payment_refund_reversal") return RotateCcw;
  return item.delta >= 0 ? ArrowUpCircle : ArrowDownCircle;
};

const formatNumber = (value: number) => Math.abs(value).toLocaleString("es-ES");

export function CreditHistorySection({ variant = "default" }: CreditHistorySectionProps) {
  const { language } = useAuth();
  const [items, setItems] = useState<CreditHistoryItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(false);

  const loadHistory = useCallback(async (cursor?: string | null) => {
    const loadingMore = Boolean(cursor);
    if (loadingMore) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    setError(false);
    try {
      const response = await creditHistoryAxios(PAGE_SIZE, cursor);
      setItems((current) => (loadingMore ? [...current, ...response.items] : response.items));
      setNextCursor(response.next_cursor);
    } catch (loadError) {
      console.error("Error fetching credit history:", loadError);
      setError(true);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const containerClass = variant === "compact" ? "border-b border-gray-100 py-4" : "";
  const content = useMemo(() => {
    if (isLoading) {
      return (
        <div className="flex min-h-[120px] items-center justify-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          {copy.loading[language]}
        </div>
      );
    }

    if (error && items.length === 0) {
      return <div className="py-8 text-center text-sm text-gray-500">{copy.failed[language]}</div>;
    }

    if (items.length === 0) {
      return <div className="py-8 text-center text-sm text-gray-500">{copy.empty[language]}</div>;
    }

    return (
      <>
        <div className="divide-y divide-gray-100">
          {items.map((item) => {
            const Icon = iconFor(item);
            const isPositive = item.delta >= 0;
            return (
              <div key={item.id} className="flex gap-3 py-3">
                <div
                  className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                    isPositive ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-700"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-950">{labelFor(item, language)}</p>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {new Date(item.occurred_at).toLocaleString(localeByLanguage[language], {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 text-sm font-semibold ${
                        isPositive ? "text-emerald-700" : "text-gray-950"
                      }`}
                    >
                      {isPositive ? "+" : "-"}
                      {formatNumber(item.delta)}
                    </span>
                  </div>
                  {item.public_reason && (
                    <p className="mt-2 break-words text-xs text-gray-500">
                      {copy.reason[language]}: {item.public_reason}
                    </p>
                  )}
                  {item.credits_before != null && item.credits_after != null && (
                    <p className="mt-1 text-xs text-gray-400">
                      {copy.beforeAfter[language]}: {item.credits_before}
                      {" -> "}
                      {item.credits_after}
                    </p>
                  )}
                  {item.credits_before == null && item.credits_after != null && (
                    <p className="mt-1 text-xs text-gray-400">
                      {copy.initialBalance[language]}: {item.credits_after}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {nextCursor && (
          <button
            type="button"
            onClick={() => loadHistory(nextCursor)}
            disabled={isLoadingMore}
            className="mt-3 inline-flex h-9 items-center gap-2 rounded-lg border border-gray-200 px-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
            {copy.loadMore[language]}
          </button>
        )}
      </>
    );
  }, [error, isLoading, isLoadingMore, items, language, loadHistory, nextCursor]);

  if (variant === "compact") {
    return (
      <div className={containerClass}>
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-white text-gray-700 ring-1 ring-gray-200">
              <History className="h-4 w-4" />
            </div>
            <h2 className="text-base font-semibold text-gray-950">{copy.title[language]}</h2>
          </div>
          {items.length > 0 && <span className="text-xs font-medium text-gray-400">{items.length}</span>}
        </div>
        {content}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="p-4 md:p-6">
        <div className="flex items-center space-x-2 md:space-x-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-200 md:h-10 md:w-10">
            <History className="h-4 w-4 text-gray-600 md:h-5 md:w-5" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 md:text-xl">{copy.title[language]}</h3>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0 md:p-6 md:pt-0">{content}</CardContent>
    </Card>
  );
}
